import {ReactElement, ReactNode} from "react";
import {styled} from "styled-components";

import {demanderContactFournisseur} from "@/Api/notification";
import IconContact from "@/assets/iconContact.svg?react";
import IconValide from "@/assets/iconValide.svg?react";
import {TableauBonus} from "@/components/Bonus/TableauBonus/TableauBonus";
import {Button} from "@/components/Button/Button";
import Loader from "@/components/Loader/Loader";
import {AppContext} from "@/context/AppContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {quicksandBold, quicksandMedium} from "@/GlobalStyle";
import {breakpoints} from "@/GlobalStyle";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {FournisseurInfo} from "@/ts/fournisseur";
import {FournisseurBonus} from "@/ts/kpi";

const HeaderContent = styled.div`
  display: grid;
  grid-template-areas:
    "left info info  info bonus bonus"
    ". . . . bonus bonus";

  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: auto;
  gap: 20px;
  width: 100%;
  @media (max-width: ${breakpoints.xl}) {
    grid-template-columns: repeat(3, 1fr);

    grid-template-areas:
      "left info info"
      "bonus bonus bonus";
  }
  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(150px, auto) auto;
    grid-template-areas:
      " left  " "info "
      "bonus  ";
  }
`;

const LeftBloc = styled.div`
  min-width: 200px;
  padding: 15px 0;
  grid-area: left;
  height: fit-content;
  margin: auto;
  & img {
    margin: 0 auto;
    width: 150px;
    display: block;
  }
  @media (max-width: ${breakpoints.md}) {
    min-width: 170px;
  }
`;
const InfoBloc = styled.div`
  border: 2px solid ${({theme}) => theme.colors.darkPrimary};
  grid-area: info;
  min-width: 300px;
  height: fit-content;
  max-width: calc(100% - 60px);
  padding: 15px 30px;
  width: 100%;
  justify-self: center;
  & h2 {
    color: ${({theme}) => theme.colors.darkPrimary};
    font-size: 1.125rem;
    text-align: center;
    ${quicksandBold}
  }
  @media (max-width: ${breakpoints.md}) {
    min-width: 210px;
    padding: 15px 15px;
    flex: none;
  }
`;
const LigneInfo = styled.p`
  color: ${({theme}) => theme.colors.darkPrimary};
  font-size: 1rem;
  ${quicksandBold}
  margin:15px 0;
  & span {
    color: ${({theme}) => theme.colors.lightPrimary};
    ${quicksandMedium}
  }
`;
const TableauBonusWrapper = styled.div`
  grid-area: bonus;
  display: flex;
  justify-content: flex-end;
  align-self: flex-start;
  flex: 1;
  @media (max-width: ${breakpoints.xl}) {
    width: 100%;
    justify-content: center;
  }
`;

const NoBonusInfo = styled.p`
  margin: auto;
  text-align: center;
  font-size: 1.25rem;
  color: ${({theme}) => theme.colors.alert};
`;

const ButtonDemanderContact = styled(Button)`
  margin: 15px auto;
  width: fit-content;
`;

export function FournisseurHeader({
  fournisseurInfo,
  bonus = [],
  contratEnCours = false,
  children,
}: {
  fournisseurInfo: FournisseurInfo;
  bonus: FournisseurBonus[];
  contratEnCours?: boolean;
  children?: ReactNode;
}): ReactElement {
  const {API, userType} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );
  const {currentCampagne} = useGetContext<UserParamContextType>(
    UserParamContext,
    "UserParamContext"
  );
  const demanderContactFournisseurLoadingState = useLoadingState();
  console.log(bonus);
  return (
    <HeaderContent data-testid="HeaderContent">
      <LeftBloc data-testid="LeftBloc">
        <img
          src={fournisseurInfo.logo}
          alt={`Logo fournisseur ${fournisseurInfo.nom}`}
        />

        {children}
      </LeftBloc>
      <InfoBloc data-testid="InfoBloc">
        <h2>Information</h2>
        <LigneInfo data-testid="LigneInfo-nom">
          Nom : <span>{fournisseurInfo.nom}</span>
        </LigneInfo>
        <LigneInfo data-testid="LigneInfo-adresse">
          Adresse : <span>{fournisseurInfo.adresse}</span>
        </LigneInfo>
        <LigneInfo data-testid="LigneInfo-telephone">
          Téléphone : <span>{fournisseurInfo.telephone}</span>
        </LigneInfo>
        <LigneInfo data-testid="LigneInfo-mail">
          Mail : <span>{fournisseurInfo.email}</span>
        </LigneInfo>
        {userType === "Adherent" && !contratEnCours ? (
          <ButtonDemanderContact
            label={
              demanderContactFournisseurLoadingState.isSuccess
                ? "DEMANDE ENVOYEE AVEC SUCCES"
                : demanderContactFournisseurLoadingState.hasError
                  ? demanderContactFournisseurLoadingState.error?.includes(
                      "Une demande de contact avec ce fournisseur a déjà été effectuée"
                    )
                    ? "Demande déjà effectuée"
                    : "LA DEMANDE A ECHOUE, REESSAYER"
                  : "DEMANDER UN CONTACT"
            }
            variant={
              demanderContactFournisseurLoadingState.isSuccess
                ? "validate"
                : demanderContactFournisseurLoadingState.hasError
                  ? "negate"
                  : "secondary"
            }
            title={demanderContactFournisseurLoadingState.error ?? undefined}
            disabled={demanderContactFournisseurLoadingState.isLoading}
            icon={
              demanderContactFournisseurLoadingState.isSuccess ? (
                <IconValide className="iconWhite" />
              ) : demanderContactFournisseurLoadingState.isLoading ? (
                <Loader isIcon={true} color="white" />
              ) : (
                IconContact
              )
            }
            onClick={async () => {
              if (demanderContactFournisseurLoadingState.isSuccess) return;
              await demanderContactFournisseurLoadingState.executeWithLoading(
                async () =>
                  await demanderContactFournisseur(
                    API,
                    fournisseurInfo.fournisseur_id,
                    Number(currentCampagne.value)
                  )
              );
            }}
          />
        ) : null}
      </InfoBloc>
      <TableauBonusWrapper data-testid="TableauBonusWrapper">
        {bonus.length > 0 ? (
          <TableauBonus bonus={bonus} />
        ) : (
          <NoBonusInfo>
            Aucun bonus pour ce fournisseur à cette campagne.{" "}
          </NoBonusInfo>
        )}
      </TableauBonusWrapper>
    </HeaderContent>
  );
}
